import React from "react";
import { Button, Dropdown } from "semantic-ui-react";
import { arrayHasItems, safelyRunFunction } from "../../../helpers/GlobalHelper";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { PayloadContentType } from "./LmsFormGenerator";
import LmsTableActionModalButton from "./LmsTableActionModalButton";
import { ITableActionCustomButton } from "./LmsTable";

const LMSScreenActionButtons = ({ ...props }) => {
  const { actionButtons, useLmsTableInstance } = props;
  const {
    setSortedData: setTableData,
    checkedItems,
    filteredData: tableData,
    getCheckedIds,
    handleActionClick,
    handlePush,
    handleDelete,
    handleArchive,
    handleActivate,
    handleDeActivate,
    handleSetACHTrue,
    handleSetACHFalse,
    handleExport,
    handlePushBack,
    handleSave,
    handleCancel,
    handleAddSelected,
    handleSubmitSelected,
    handleDownloadSelected,
    handleSendEmail,
    activeButtonText,
    submitting,
    inputs,
    refreshTableData
  } = useLmsTableInstance || {};

  const defaultButtons = {
    add: {
      text: "Add", icon: GlobalConstants.Icons.ADD, handleClick: ({ path }) => handleActionClick(path ? { path } : { action: "add" })
    },
    edit: {
      text: "Edit",
      icon: GlobalConstants.Icons.EDIT,
      handleClick: ({ firstCheckedRow, path = "" }) => handleActionClick({ path, id: firstCheckedRow?.id || firstCheckedRow?.Id, action: "edit" }),
      isDisabled: (checkedItems) => checkedItems?.length !== 1,
    },
    remove: {
      text: "Delete",
      icon: GlobalConstants.Icons.DELETE,
      handleClick: () => handleDelete(),
      isDisabled: (checkedItems) => checkedItems?.length === 0,
    },
    copy: {
      text: "Copy",
      icon: GlobalConstants.Icons.COPY,
      handleClick: ({ firstCheckedRow, path }) => handleActionClick({ path, id: firstCheckedRow?.id, action: "copy" }),
      isDisabled: (checkedItems) => checkedItems?.length !== 1,
    },
    close: {
      text: "Close",
      icon: GlobalConstants.Icons.CLOSE,
      handleClick: handlePushBack
    },
    activate: {
      text: "Activate",
      icon: GlobalConstants.Icons.TOGGLE_ON,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleActivate()
    },
    deactivate: {
      text: "De-Activate",
      icon: GlobalConstants.Icons.TOGGLE_OFF,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleDeActivate()
    },
    archive: {
      text: "Archive",
      icon: GlobalConstants.Icons.ARCHIVE,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleArchive()
    },
    save: {
      text: "Save",
      icon: GlobalConstants.Icons.SAVE,
      isDisabled: () => Object.keys(inputs).length === 0,
      handleClick: () => handleSave()
    },
    cancel: {
      text: "Cancel",
      icon: GlobalConstants.Icons.CANCEL,
      isDisabled: () => Object.keys(inputs).length === 0,
      handleClick: () => handleCancel()
    },
    addselected: {
      text: "Add Selected",
      icon: GlobalConstants.Icons.ADD,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleAddSelected()
    },
    submitselected: {
      text: "Add Selected",
      icon: GlobalConstants.Icons.ADD,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
    },
    downloadselected: {
      text: "Download",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: (checkedItems) => checkedItems?.length !== 1,
    },
    setachtrue: {
      text: "Set ACH True",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleSetACHTrue()
    },
    setachfalse: {
      text: "Set ACH False",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleSetACHFalse()
    },
    sendemail: {
      text: "Send Email",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: (checkedItems) => checkedItems?.length === 0,
      handleClick: () => handleSendEmail()
    },
    modalbutton: {
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: (checkedItems) => checkedItems?.length !== 1,
    }
  };

  return (
    <React.Fragment>
      {actionButtons && (
        <div className="lms-action-buttons-container">
          {actionButtons.map((ab, i) => {
            const firstCheckedRow = useLmsTableInstance?.firstCheckedRow || tableData[checkedItems[0]];
            const clickOptions = {
              ...useLmsTableInstance,
              firstCheckedRow,
              id: firstCheckedRow?.id || "",
              tableData: tableData,
              checkedItems,
              setTableData,
              path: typeof ab?.path === "function" ? ab?.path(firstCheckedRow, useLmsTableInstance) : ab?.path,
            };

            if (React.isValidElement(ab)) {
              return ab;
            }
            else {
              const action = typeof ab === "string" ? ab : ab?.action ? ab?.action : ab?.text ? ab?.text : "";
              const actionLower = action?.toLowerCase();
              if (actionLower === "export") {
                return <Dropdown
                  key={"export-dropdown"}
                  text={"Export"}
                  icon={GlobalConstants.Icons.EXPORT}
                  loading={submitting && activeButtonText === "Export"}
                  disabled={submitting ? submitting : !arrayHasItems(tableData) || checkedItems?.length === 0}
                  button
                  compact
                >
                  <Dropdown.Menu>
                    <Dropdown.Item
                      icon={GlobalConstants.Icons.FILE_OUTLINE}
                      text={"Export to CSV"}
                      onClick={() => handleExport(ab, "Csv")}
                    />
                    <Dropdown.Item
                      icon={GlobalConstants.Icons.EXCEL_OUTLINE}
                      text={"Export to Excel"}
                      onClick={() => handleExport(ab, "Excel")}
                    />
                    <Dropdown.Item
                      icon={GlobalConstants.Icons.PDF_OUTLINE}
                      text={"Export to PDF"}
                      onClick={() => handleExport(ab, "Pdf")}
                    />
                  </Dropdown.Menu>
                </Dropdown>;
              }
              else if (actionLower === GlobalConstants.TableDefaultActions.SUBMIT_SELECTED) {
                const {
                  text = "",
                  icon = "",
                  isDisabled = () => false,
                  submitSuffix = "",
                  method = "POST",
                  data = undefined,
                  payloadContentType = "JSON" as PayloadContentType,
                  onSuccessFunc = undefined as any,
                  clearSuffixes = [] as string[],
                  refreshDataOnSuccess = false,
                  handleFormatSubmitSelectedRows = undefined,
                  confirmationMessage = undefined as string | undefined,
                  alertMessage = undefined as string | undefined,
                } = { ...defaultButtons[actionLower], ...ab } as ITableActionCustomButton;
                return (
                  <Button
                    key={`${text}-${i}`}
                    content={text}
                    icon={icon}
                    loading={submitting && text === activeButtonText}
                    disabled={submitting ? submitting : typeof isDisabled === "function" ? isDisabled(checkedItems, tableData) : false}
                    onClick={() => handleSubmitSelected({
                      submitSuffix,
                      method,
                      data,
                      buttonText: text,
                      payloadDataType: payloadContentType,
                      onSuccessFunc,
                      clearSuffixes,
                      refreshDataOnSuccess,
                      handleFormatSubmitSelectedRows,
                      confirmationMessage,
                      alertMessage
                    })}
                    compact
                  />
                );
              }
              else if (actionLower === GlobalConstants.TableDefaultActions.DOWNLOAD_SELECTED) {
                const {
                  text = "",
                  icon = "",
                  isDisabled = () => false,
                  downloadSuffix = "",
                  downloadMany = false,
                  method = "GET",
                  payloadContentType = "FormData" as PayloadContentType,
                  refreshDataOnSuccess = false,
                  onDownloadSuccessFunc = undefined as any,
                  fileName = undefined as string | undefined,
                } = { ...defaultButtons[actionLower], ...ab } as ITableActionCustomButton;
                const selectedId = !downloadMany ? tableData[checkedItems[0]]?.id : undefined;
                const data = downloadMany ? getCheckedIds() : undefined;
                let onDownloadSuccess = undefined as any;
                if (refreshDataOnSuccess || onDownloadSuccessFunc) {
                  onDownloadSuccess = () => {
                    safelyRunFunction(onDownloadSuccessFunc);
                    safelyRunFunction(refreshDataOnSuccess ? () => refreshTableData() : undefined);
                  };
                }

                return (
                  <Button
                    key={`${text}-${i}`}
                    content={text}
                    icon={icon}
                    loading={submitting && text === activeButtonText}
                    disabled={submitting ? submitting : typeof isDisabled === "function" ? isDisabled(checkedItems, tableData) : false}
                    compact
                    onClick={() => {
                      return handleDownloadSelected(downloadSuffix, {
                        fileName: fileName,
                        id: selectedId,
                        method,
                        data,
                        contentType: payloadContentType,
                        onSuccessFunc: onDownloadSuccess,
                        buttonText: text
                      });
                    }}
                  />
                );
              } else if (actionLower === GlobalConstants.TableDefaultActions.MODAL_BUTTON || ab?.modalContent || ab?.isIframeModal) {
                const {
                  text,
                  icon,
                  isDisabled,
                  modalContent,
                  handleClick
                } = { ...defaultButtons[actionLower], ...ab } as ITableActionCustomButton;
                return (
                  <LmsTableActionModalButton
                    modalContent={modalContent}
                    modalSize={ab?.modalSize}
                    icon={icon}
                    content={text}
                    useLmsTableInstance={{ ...useLmsTableInstance, firstCheckedRow }}
                    isDisabled={typeof isDisabled === "function" ? isDisabled(checkedItems, tableData) : false}
                    handleButtonClick={handleClick}
                    isIframeModal={ab?.isIframeModal}
                    path={ab?.path}
                    iframeHeight={ab?.height}
                  />
                );
              }
              else if (defaultButtons[actionLower]) {
                const { text = "", icon = "", isDisabled = () => false, handleClick = () => null } = { ...defaultButtons[actionLower], ...ab } as ITableActionCustomButton;
                return (
                  <Button
                    key={`${text}-${i}`}
                    content={text}
                    icon={icon}
                    loading={submitting && text === activeButtonText}
                    disabled={submitting ? submitting : typeof isDisabled === "function" ? isDisabled(checkedItems, tableData) : false}
                    onClick={() => handleClick(clickOptions)}
                    compact
                  />
                );
              }
              else if (ab?.text) {
                const handleClick = () => {
                  if (ab.handleClick) {
                    return ab.handleClick(clickOptions);
                  }
                  if (handlePush && ab.path) {
                    return handlePush(ab.path);
                  }
                };
                return (
                  <Button
                    key={`${ab?.text}-${i}`}
                    compact
                    content={ab?.text || ""}
                    icon={ab?.icon || ""}
                    loading={submitting && ab?.text === activeButtonText}
                    disabled={typeof ab?.isDisabled === "function" ? ab?.isDisabled(checkedItems, tableData) : ab?.isDisabled}
                    onClick={() => handleClick()}
                  />
                );
              }
              else {
                return <React.Fragment key={`blank-element-${i}`} />;
              }
            }
          })}
        </div>
      )}
    </React.Fragment>
  );
};

export default LMSScreenActionButtons;